"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, SunMedium, Flame, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Pilgrimage Surges & Booking Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage ultra-high demand spikes around Gurpurabs, Baisakhi, and national holiday weekends against mid-week dips. We deploy minimum length-of-stay fences and dynamic surge pricing so you never under-price high-occupancy windows.",
  metricLabel: "Strategy focus",
  metricValue: "Dynamic length-of-stay controls & compression pacing"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate movements across Mall Road, Golden Temple perimeter, and airport corridor hotels on MakeMyTrip, Agoda, and Booking.com. Automated alerts preserve your ADR while capturing early high-value bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA competitor tracking & yield protection"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Eliminate unauthorized B2B package leakage and reduce excessive commission burn. We secure strict meta-search rate parity and drive direct direct-to-hotel bookings through tailored value adds for pilgrimage and family travelers.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking conversion"
}];
const seasonalitySeasons = [{
  season: "Peak Season",
  months: "October – March",
  focus: "Spiritual Festivals, NRI Tourism & Winter Influx",
  strategy: "Yield maximization, strict multi-night stay restrictions, and zero panic discounting during Gurpurab and Diwali.",
  icon: Sparkles
}, {
  season: "Value Season",
  months: "April – June",
  focus: "Corporate, Transit & Summer Pilgrimage",
  strategy: "Targeted corporate packages, wedding banquet room blocks, and value-add meal bundles to stabilize baseline ADR.",
  icon: Flame
}, {
  season: "Monsoon & Shoulder",
  months: "July – September",
  focus: "Weekend Getaways & Extended Heritage Stays",
  strategy: "Short-lead flash offers, regional drive-market promotions from Delhi-NCR/Punjab, and direct channel perks.",
  icon: SunMedium
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapeamritsar" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Amritsar
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Amritsar presents a vibrant, high-velocity micro-market shaped by continuous pilgrimage to the Sri Harmandir Sahib (Golden Temple), seasonal NRI returnees, and intense weekend leisure flows from Delhi-NCR and Punjab. Sunil Singh implements disciplined, algorithm-backed revenue structures crafted specifically for Amritsar hotels, boutique stays, and heritage properties.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-lg border border-border bg-card/60 p-6 sm:p-8 lg:p-10 shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border/70">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Demand Calendar
              </span>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground mt-1">
                Amritsar Hospitality Seasonality Blueprint
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
              Precise rate fencing aligned with regional festival calendars, border ceremonies, and weather patterns.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
            {seasonalitySeasons.map((period, idx) => {
            const IconComp = period.icon;
            return <div key={period.season} data-index={idx} className="rounded-md border border-border/70 bg-background/80 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="p-1.5 rounded-md bg-secondary/10 text-secondary">
                        <IconComp className="size-4 text-chart-2" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-foreground leading-none">
                          {period.season}
                        </h4>
                        <span className="text-xs text-muted-foreground font-mono">
                          {period.months}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs font-medium text-secondary mb-2">
                      {period.focus}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {period.strategy}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Amritsar hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}